import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { createContext, ReactNode, useContext } from "react"

import { API } from "@/api"
import { APIQueries } from "@/api/queries"
import { Organization } from "@/api/resources/organization/types"
import FullPageLoading from "@/components/full-page-loading"
import { useClubId } from "@/lib/hooks/use-club-id"

import { useAuth } from "./auth-context"

type GlobalContextValue = {
  organization: UseQueryResult<Organization> | undefined
}

const GlobalContext = createContext<GlobalContextValue | undefined>(undefined)

// eslint-disable-next-line react-refresh/only-export-components
export const useGlobalContext = () => {
  const context = useContext(GlobalContext)
  if (!context) {
    throw new Error(
      "useGlobalContext must be used within a GlobalContextProvider",
    )
  }
  return context
}

export const GlobalContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const { isAuthenticated } = useAuth()
  const clubId = useClubId()
  const params = { fields: ["name", "logo"] }

  const organization = useQuery({
    queryKey: APIQueries.organization.retrieve({ id: clubId }, params).queryKey,
    queryFn: () =>
      API.organization.retrieve({
        urlParams: {
          id: clubId,
        },
        params,
      }),
    enabled: isAuthenticated && !!clubId,
    refetchOnMount: false,
  })

  if (isAuthenticated && organization.isLoading) {
    return <FullPageLoading />
  }

  return (
    <GlobalContext.Provider
      value={{
        organization,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}
